import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useTheme } from '@/contexts/ThemeContext';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { ChevronDown, Users } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Master {
    key: string;
    label: string;
}

interface Professor {
    id: number;
    name: string;
    bio: string | null;
    avatar_url: string | null;
    masters: Master[];
}

interface Props {
    professors: Professor[];
    masters: Master[];
    default_master: string | null;
}

interface MasterDropdownProps {
    masters: Master[];
    activeMaster: string | null;
    onChange: (key: string | null) => void;
}

interface DropdownItemProps {
    label: string;
    isActive: boolean;
    isFinance: boolean;
    gradientStyle: React.CSSProperties;
    primaryBg: string;
    onClick: () => void;
}

const DropdownItem: React.FC<DropdownItemProps> = ({ label, isActive, isFinance, gradientStyle, primaryBg, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const showGradient = isFinance && (isActive || hovered);
    const showPrimary = !isFinance && isActive;

    return (
        <li>
            <button
                type="button"
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={`w-full px-5 py-3 text-left text-sm font-medium transition-colors duration-100 ${
                    showGradient
                        ? 'text-black'
                        : showPrimary
                          ? `${primaryBg} text-white`
                          : 'text-gray-700'
                }`}
                style={showGradient ? gradientStyle : undefined}
            >
                {label}
            </button>
        </li>
    );
};

const MasterDropdown: React.FC<MasterDropdownProps> = ({ masters, activeMaster, onChange }) => {
    const colors = useThemeColors();
    const { theme } = useTheme();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const activeLabel = masters.find((m) => m.key === activeMaster)?.label ?? null;

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (key: string) => {
        onChange(key);
        setOpen(false);
    };

    const gradientStyle = { background: 'linear-gradient(to right, var(--color-theme-primary), var(--color-theme-secondary))' };
    const activeStyle = theme === 'cfinance' ? gradientStyle : undefined;

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`flex max-w-[265px] items-start gap-2 rounded-xl px-4 py-2 text-left text-sm font-medium shadow-sm transition-all duration-150 focus:outline-none ${
                    activeMaster
                        ? theme === 'cfinance'
                            ? 'text-black'
                            : `${colors.primaryBg} text-white`
                        : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:shadow-md'
                }`}
                style={activeMaster ? activeStyle : undefined}
            >
                <span className="flex-1 leading-snug">{activeLabel}</span>
                <ChevronDown
                    className={`mt-0.5 h-4 w-4 shrink-0 transition-transform duration-200 ${activeMaster && theme === 'cfinance' ? 'text-black/70' : activeMaster ? 'text-white/80' : 'text-gray-500'} ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="absolute top-full left-0 z-20 mt-2 w-72 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
                    <ul className="py-1">
                        {masters.map((master) => {
                            const isActive = activeMaster === master.key;
                            return (
                                <DropdownItem
                                    key={master.key}
                                    label={master.label}
                                    isActive={isActive}
                                    isFinance={theme === 'cfinance'}
                                    gradientStyle={gradientStyle}
                                    primaryBg={colors.primaryBg}
                                    onClick={() => handleSelect(master.key)}
                                />
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

const ProfessorsGalleryContent: React.FC<Props> = ({ professors, masters = [], default_master }) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { theme } = useTheme();
    const [activeMaster, setActiveMaster] = useState<string | null>(default_master ?? masters[0]?.key ?? null);

    const filtered =
        activeMaster === null
            ? professors
            : professors.filter((p) => p.masters.some((m) => m.key === activeMaster));

    return (
        <AppLayout>
            <PageTitle title={t('professors.pageTitle')} />
            <div className="mx-auto max-w-6xl">
                {/* Page Header */}
                <div className="relative mb-10 text-center">
                    <div
                        className={`mx-auto mb-6 h-1 w-16 rounded-full bg-gradient-to-r ${colors.gradientFrom} ${colors.gradientTo}`}
                    />
                    <h1 className="mb-3 text-4xl font-bold text-gray-900">
                        {t('professors.title')}
                    </h1>
                    <p className="text-lg text-gray-600">{t('professors.description')}</p>

                    {/* Mobile: below description, full width */}
                    {masters.length > 0 && (
                        <div className="mt-4 flex justify-center sm:hidden">
                            <MasterDropdown
                                masters={masters}
                                activeMaster={activeMaster}
                                onChange={setActiveMaster}
                            />
                        </div>
                    )}

                    {/* Desktop: absolute left edge, aligned with title */}
                    {masters.length > 0 && (
                        <div className="absolute top-8 left-0 hidden sm:block">
                            <MasterDropdown
                                masters={masters}
                                activeMaster={activeMaster}
                                onChange={setActiveMaster}
                            />
                        </div>
                    )}
                </div>

                {professors.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="py-12 text-center">
                            <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                            <h3 className="mb-2 text-2xl font-semibold text-gray-900">
                                {t('professors.noProfessorsTitle')}
                            </h3>
                            <p className="text-lg text-gray-600">
                                {t('professors.noProfessorsDescription')}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {filtered.length === 0 ? (
                            <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                                <div className="py-12 text-center">
                                    <Users className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                                    <p className="text-lg text-gray-600">{t('professors.noResults')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                                {filtered.map((professor) => (
                                    <div
                                        key={professor.id}
                                        className="flex flex-col items-center text-center"
                                    >
                                        {/* Avatar — gradient background, PNG transparent shows through */}
                                        <div
                                            className="mb-5 h-48 w-48 overflow-hidden rounded-full"
                                            style={
                                                theme === 'cfinance'
                                                    ? { backgroundImage: 'linear-gradient(225deg, var(--color-theme-secondary), var(--color-theme-primary))' }
                                                    : { backgroundImage: 'linear-gradient(225deg, #ff4000, #ffbf00)' }
                                            }
                                        >
                                            <img
                                                src={professor.avatar_url || ''}
                                                alt={professor.name}
                                                className="h-full w-full object-cover"
                                            />
                                        </div>

                                        {/* Name */}
                                        <h3 className="mb-3 text-2xl font-bold text-gray-900">
                                            {professor.name}
                                        </h3>

                                        {/* Bio */}
                                        {professor.bio && (
                                            <p className="text-base leading-relaxed text-gray-600">
                                                {professor.bio}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </AppLayout>
    );
};

const ProfessorsGallery: React.FC<Props> = ({ professors, masters, default_master }) => (
    <ThemeWrapper>
        <ProfessorsGalleryContent professors={professors} masters={masters} default_master={default_master} />
    </ThemeWrapper>
);

export default ProfessorsGallery;
